'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Button } from '../../../components/button';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';

export type ProfileListCardItem = {
  key: string;
  label: string;
  value?: string;
};

export type ProfileListCardProps = {
  title: string;
  tooltip: string;
  items: ProfileListCardItem[];
  emptyLabel: string;
  emptyValue: string;
  addLabel: string;
  isReadOnly: boolean;
  isSaving: boolean;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  children: ReactNode;
};

export function ProfileListCard({
  title,
  tooltip,
  items,
  emptyLabel,
  emptyValue,
  addLabel,
  isReadOnly,
  isSaving,
  onAdd,
  onEdit,
  onDelete,
  children,
}: ProfileListCardProps) {
  const tCommon = useTranslations('designSystem.common');
  const hasItems = items.length > 0;

  return (
    <SettingsSection title={title} tooltip={tooltip}>
      <SettingsCard>
        {hasItems ? (
          items.map((item, index) => (
            <SettingsRow
              key={item.key}
              label={item.label}
              value={item.value}
              control={
                isReadOnly ? undefined : (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => onDelete(index)}
                    disabled={isSaving}
                  >
                    {tCommon('delete')}
                  </Button>
                )
              }
              action={
                isReadOnly
                  ? undefined
                  : {
                      label: tCommon('edit'),
                      onClick: () => onEdit(index),
                      disabled: isSaving,
                    }
              }
              isReadOnly={isReadOnly}
            />
          ))
        ) : (
          <SettingsRow label={emptyLabel} value={emptyValue} />
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={onAdd}
          disabled={isReadOnly || isSaving}
          alignSelf="flex-start"
          mt={hasItems ? 2 : 0}
        >
          {addLabel}
        </Button>
      </SettingsCard>
      {children}
    </SettingsSection>
  );
}
